<template>
  <button
    type="button"
    class="btn btn-primary btn-with-icon"
    @click="exportDocx"
    :title="$t('src/components/protocol.5389')"
  >
    <svg-icon class="svg-icon" :name="'protocol'" :height="20" :width="25"></svg-icon>
    {{ $t('src/components/protocol.protocol') }}
  </button>
</template>

<script>
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts.js'
import { patientMaxHR } from '@/mixins/patient-max-hr'
import { fullName } from '@/mixins/full-name.js'
import { courseTypeList } from '@/modules/course-type-list.js'
import { courseDate } from '@/mixins/course-date'
import { DATE_FORMAT, unitDuration, courseTypes } from '@/main.config'
import { trainingDate } from '@/mixins/training-date'
import getAverageOfArray from '@/modules/average-of-array.js'
import { userRights } from '@/mixins/user-rights.js'

export default {
  name: 'ProtocolCourse',
  components: {
    SvgIcon: () => import('@/components/SvgIcon.vue')
  },
  mixins: [patientMaxHR, fullName, courseDate, trainingDate, userRights],
  props: {
    course: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      required: true
    },
    trainings: {
      type: Array,
      required: true
    },
    trainingsInfo: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    loading: true
  }),
  computed: {
    localeRU() {
      if (this.$i18n.locale === 'ru') {
        return true
      } else return false
    },
    fullNamePatient() {
      return this.getFullName(this.patient)
    },
    gender() {
      if (this.patient.gender === 0) {
        return this.$t('src/components/protocol.male')
      } else if (this.patient.gender === 1) {
        return this.$t('src/components/protocol.female')
      } else return '–'
    },
    height() {
      if (this.patient.height) return `${this.patient.height} ${this.$t('unit.cm')}`
      else return '-'
    },
    weight() {
      if (this.patient.weight) return `${this.patient.weight} ${this.$t('unit.kg')}`
      else return '-'
    },
    body() {
      let body = [
        [
          {
            text: this.$t('general.date'),
            style: 'tableHeader',
            alignment: 'center'
          },
          {
            text: `${this.$t('general.duration')}, ${this.$tc('unit.min', 2)}`,
            style: 'tableHeader',
            alignment: 'center'
          },
          {
            text: `${this.$t('sessionsInfo.averageHeartRate')}\n ${this.$t('unit.bpm')}`,
            style: 'tableHeader',
            alignment: 'center'
          },
          {
            text: `${this.$t('sessionsInfo.maxHeartRate')}\n ${this.$t('unit.bpm')}`,
            style: 'tableHeader',
            alignment: 'center'
          },
          {
            text: `${this.$t('sessionsInfo.averagePace')}\n ${this.$t('unit.sm')}`,
            style: 'tableHeader',
            alignment: 'center'
          },
          { text: 'SpO2\n %', style: 'tableHeader', alignment: 'center' },
          {
            text: `${this.$t('src/components/coursedatatable.3907')}\n ${this.$t('unit.m')}`,
            style: 'tableHeader',
            alignment: 'center'
          },
          {
            text: `${this.$t('src/components/coursedatatable.8698')}\n %`,
            style: 'tableHeader',
            alignment: 'center'
          },
          {}
        ]
      ]
      this.trainings.forEach(train => {
        let row = new Array()
        row.push(
          this.moment
            .utc(train.date_start, DATE_FORMAT.serverFull)
            .local()
            .format(DATE_FORMAT[`${this.$store.getters.LOCALE}`])
        )
        row.push(this.getTrainDuration(train))
        let avrHR = train.rhytmogram_avr_hr ? `${train.rhytmogram_avr_hr}` : '–'
        row.push(avrHR)
        let axHR = train.rhytmogram_max_hr ? `${train.rhytmogram_max_hr}` : '–'
        row.push(axHR)
        row.push(this.getAverageStepRate(train))
        let saturation = this.saturationStr(train)
        row.push(saturation)
        let distance = train.distance ? `${train.distance}` : '–'
        row.push(distance)
        let zonePercent = train.rhytmogram_zone_percent ? `${train.rhytmogram_zone_percent}` : '–'
        row.push(zonePercent)

        if (train.training_type === 1) {
          row.push({
            fontSize: 7,
            fillColor: '#9899A4',
            color: '#fff',
            text: '6M\nWT'
          })
        } else row.push({})

        body.push(row)
      })
      return body
    },
    courseType() {
      if (!this.course.course_type) {
        return courseTypeList[courseTypes.rehab].title
      }
      return courseTypeList[this.course.course_type].title
    },
    courseDate() {
      if (!this.courseMomentStartDate) {
        return ''
      } else {
        return this.courseMomentStartDate.format(DATE_FORMAT[`${this.$store.getters.LOCALE}`])
      }
    },
    courseCloseDate() {
      if (!this.courseMomentEndDate) {
        return ''
      } else {
        return this.courseMomentEndDate.format(DATE_FORMAT[`${this.$store.getters.LOCALE}`])
      }
    },
    hrValue() {
      if (!+this.course.opt_watch_hr) {
        return this.OffString
      }
      return +this.course.course_hr_min + '-' + +this.course.course_hr_max
    },
    optBorg() {
      if (!+this.course.opt_borg) {
        return this.OffString
      } else return this.OnString
    },
    optAutoSessionStop() {
      if (!+this.course.auto_session_stop) {
        return this.OffString
      } else return this.OnString
    },
    optArrhythmia() {
      if (!+this.course.opt_calc_arrythmias) {
        return this.OffString
      } else return this.OnString
    },
    optCalcST() {
      if (!+this.course.opt_calc_st) {
        return this.OffString
      } else return this.OnString
    },
    optShowEvents() {
      if (!+this.course.opt_show_events) {
        return this.OffString
      } else return this.OnString
    },
    durationSettingString() {
      if (this.course.training_duration === null) {
        return this.OffString
      }
      switch (this.course.training_duration_unit) {
        case unitDuration.hours:
          return this.course.training_duration + ` ${this.$t('unit.h')}`
        case unitDuration.minutes:
          return this.course.training_duration + ` ${this.$tc('unit.min', 2)}`
        default:
          return ''
      }
    },
    isExistsDoctorReport() {
      return this.course && this.course.doctor_report
    },
    conclusion() {
      if (this.isExistsDoctorReport) return this.course.doctor_report
      else return this.$t('src/pages/coursesedit.notConclusion')
    },
    OffString() {
      return this.$t('src/components/protocol.off')
    },
    OnString() {
      return this.$t('src/components/protocol.on')
    }
  },
  methods: {
    getTrainDuration(training) {
      let duration = this.getDoneTrainingDuration(training)
      duration = Math.floor(duration / 60)
      return duration
    },
    getAverageStepRate(training) {
      const stepRate = JSON.parse(training.step_rates)
      if (!stepRate || stepRate.rateValues.length === 0) {
        return '—'
      }
      return `${getAverageOfArray(stepRate.rateValues)}`
    },
    async exportDocx() {
      pdfMake.vfs = pdfFonts.pdfMake.vfs
      const logoNSEng = `<svg xmlns="http://www.w3.org/2000/svg" width="88" height="20" viewBox="0 0 88 20" fill="none">
                          <g clip-path="url(#clip0_8_2)">
                            <path d="M9.06216 13.6049C8.13961 13.6049 7.39362 12.8357 7.39362 11.8844C7.39362 10.9331 8.13961 10.1639 9.06216 10.1639C9.98472 10.1639 10.7307 10.9331 10.7307 11.8844C10.7307 12.8357 9.98472 13.6049 9.06216 13.6049ZM18.0317 11.7433C18.0317 11.7433 17.4269 11.4884 17.2371 11.3792C15.2596 10.2868 14.1561 8.51616 14.1737 6.4224C14.1737 6.35412 14.1737 6.28585 14.1737 6.21757C14.1737 6.1493 14.1781 6.08102 14.1825 6.01275C14.1825 5.94447 14.1914 5.8762 14.1958 5.80337C14.2002 5.73509 14.209 5.66227 14.2134 5.59399C14.2223 5.52572 14.2267 5.45744 14.2355 5.38462C14.2443 5.31634 14.2532 5.24352 14.262 5.17524C14.2708 5.10697 14.2841 5.03869 14.2929 4.96586C14.3017 4.89759 14.315 4.82931 14.3282 4.76104C14.3415 4.69276 14.3503 4.62449 14.3679 4.55621C14.3812 4.48794 14.3944 4.41966 14.4077 4.35139C14.4209 4.28311 14.4342 4.21484 14.4518 4.14656C14.4651 4.07829 14.4827 4.01457 14.496 3.94629C14.5136 3.87802 14.5268 3.81429 14.5445 3.74602C14.5622 3.6823 14.5798 3.61402 14.5931 3.5503C14.6107 3.48657 14.6284 3.42285 14.646 3.35458C14.6637 3.29085 14.6813 3.22713 14.699 3.16341C14.7167 3.09968 14.7343 3.04051 14.752 2.97679C14.7696 2.91762 14.7873 2.85389 14.8049 2.79472C14.8226 2.73555 14.8403 2.67638 14.8623 2.61721C14.88 2.55803 14.902 2.49886 14.9197 2.43969C14.9374 2.38052 14.9594 2.3259 14.9771 2.27128C14.9947 2.21666 15.0168 2.16204 15.0345 2.10742C15.0521 2.0528 15.0698 2.00273 15.0919 1.94811C15.1095 1.89804 15.1272 1.84342 15.1492 1.79336C15.1669 1.74329 15.1846 1.69322 15.2022 1.64315C15.2199 1.59308 15.2375 1.54757 15.2552 1.50205C15.2728 1.45653 15.2905 1.41102 15.3081 1.3655C15.3258 1.31998 15.3435 1.27902 15.3611 1.23805C15.3788 1.19709 15.392 1.15612 15.4097 1.11516C15.4273 1.07874 15.4406 1.03778 15.4582 1.00137C15.4715 0.964953 15.4891 0.92854 15.5024 0.892127C15.5156 0.860265 15.5289 0.828403 15.5421 0.79199C15.5553 0.760128 15.5686 0.732818 15.5818 0.700957C15.5951 0.673647 15.6039 0.646337 15.6171 0.619027C15.626 0.596269 15.6392 0.568959 15.648 0.5462C15.6569 0.523442 15.6657 0.505235 15.6745 0.482477C15.6833 0.46427 15.6922 0.446064 15.6966 0.427857C15.7231 0.368686 15.754 0.304962 15.7716 0.241239V0.227584C15.7893 0.182067 15.7849 0.127447 15.7584 0.0864825C15.7363 0.0500693 15.6966 0.0182076 15.6569 0.0091043C15.6127 -0.00455069 15.5642 9.75734e-07 15.5289 0.0273109C15.4891 0.0500693 15.4626 0.0910342 15.4317 0.127448C15.4008 0.168412 15.3744 0.209377 15.3479 0.250342C15.317 0.300411 15.2817 0.345927 15.2508 0.395996C15.2066 0.459719 15.1625 0.523442 15.1139 0.591717C15.0565 0.669095 14.9992 0.751025 14.9418 0.828403C14.8756 0.919437 14.8049 1.01047 14.7387 1.1015C14.6593 1.20164 14.5842 1.30633 14.5048 1.40646C14.4165 1.5157 14.3282 1.62494 14.2399 1.73418C14.1428 1.84798 14.0457 1.96632 13.9486 2.08011C13.8427 2.19845 13.7367 2.3168 13.6308 2.43514C13.5204 2.55803 13.4057 2.67638 13.2909 2.79472C13.1717 2.91762 13.0525 3.03596 12.9289 3.1543C12.8053 3.27265 12.6773 3.39099 12.5493 3.50478C12.4169 3.61857 12.2889 3.73236 12.1521 3.8416C12.0152 3.95084 11.8784 4.05553 11.7415 4.16022C11.6091 4.2558 11.4723 4.35139 11.331 4.44242C10.1348 5.21165 9.35349 5.29358 9.04892 5.32545C7.2612 5.52572 5.78688 4.59718 5.39402 4.34229C5.08945 4.14656 4.85109 3.95084 4.69218 3.80974C4.58624 3.72781 4.43616 3.81885 4.45382 3.95084C4.45382 3.95084 4.60831 5.21621 4.63038 5.63951C4.68335 6.55439 4.63921 7.66955 3.97709 8.85298C3.2002 10.2503 1.91569 10.9012 1.59346 11.0605C1.16088 11.279 0.0838318 11.7478 0.0838318 11.7478C-0.0353495 11.8025 -0.0353495 11.98 0.0838318 12.0346C0.0838318 12.0346 1.22268 12.5353 1.59346 12.7219C2.48953 13.168 3.30173 13.7324 3.96826 14.9158C4.74956 16.3086 4.63038 17.7196 4.63038 18.1429C4.63038 18.6391 4.45382 19.8316 4.45382 19.8316C4.44057 19.9681 4.58624 20.0546 4.69218 19.9727C4.69218 19.9727 5.68094 19.208 6.02083 18.9668C6.7315 18.4706 7.71585 17.9472 9.03126 17.9426C10.6027 17.9335 11.7989 18.7574 12.0903 18.9668C12.4257 19.2126 13.4189 19.9727 13.4189 19.9727C13.5248 20.0546 13.6705 19.9636 13.6573 19.8316C13.6573 19.8316 13.4984 18.5662 13.4807 18.1429C13.4366 17.1416 13.4719 16.1083 14.1428 14.9158C14.9241 13.5275 16.1998 12.8812 16.5176 12.7219C16.9458 12.5034 18.0273 12.0346 18.0273 12.0346C18.1464 11.98 18.1464 11.8025 18.0273 11.7478L18.0317 11.7433Z" fill="#0AADF9"/>
                            <path d="M73.3363 15.4028C73.9322 15.4028 74.378 15.1934 74.6693 14.7792C74.9651 14.365 75.1107 13.8279 75.1107 13.1771C75.1107 12.5262 74.9607 11.9618 74.6649 11.5567C74.3647 11.1516 73.9233 10.9513 73.3363 10.9513C72.9302 10.9513 72.5991 11.0469 72.3387 11.2335C72.0782 11.4201 71.8884 11.6841 71.7604 12.0164C71.6368 12.3487 71.5706 12.7355 71.5706 13.1771C71.5706 13.8553 71.7207 14.3969 72.0164 14.7974C72.3122 15.198 72.7536 15.3983 73.3318 15.3983L73.3363 15.4028ZM48.5774 16.9914V9.36732H50.2592V10.6236C50.2857 10.5781 50.3387 10.4688 50.4711 10.2913C50.6344 10.0683 50.8375 9.88621 51.0758 9.74055C51.2568 9.62676 51.4554 9.53573 51.6717 9.47201C51.888 9.40828 52.1087 9.36732 52.3383 9.34911C52.5678 9.33091 52.7973 9.34001 53.0269 9.36732V11.2016C52.815 11.1334 52.5722 11.1106 52.2941 11.1334C52.016 11.1561 51.7644 11.2198 51.5393 11.3336C51.3142 11.4383 51.1244 11.5794 50.9699 11.7569C50.8154 11.9345 50.7006 12.1393 50.6212 12.3805C50.5417 12.6172 50.502 12.8858 50.502 13.1862V16.9959H48.5819L48.5774 16.9914ZM77.7725 9.36732H78.9643C78.9643 9.22166 78.9687 9.06236 78.9775 8.88939C78.9863 8.6436 79.0261 8.39781 79.0923 8.14747C79.1585 7.89713 79.2865 7.66955 79.4675 7.46928C79.7014 7.20528 79.953 7.02321 80.2267 6.92763C80.5004 6.83204 80.7652 6.77742 81.0213 6.77287C81.2773 6.76377 81.498 6.76377 81.679 6.76377H82.6192V8.32044H81.7496C81.4494 8.32044 81.2287 8.39781 81.0831 8.55257C80.9374 8.70733 80.8668 8.88939 80.8668 9.10332V9.37642H82.6192V10.8921H80.8668V16.9959H78.9599V10.8921H77.768V9.37642L77.7725 9.36732ZM45.2183 16.9686V16.1629C45.0373 16.3678 44.8343 16.5498 44.6136 16.7C44.1236 17.0323 43.51 17.2007 42.7684 17.2007C42.177 17.2007 41.6914 17.096 41.3162 16.8867C40.941 16.6773 40.6497 16.4133 40.4422 16.0947C40.2347 15.7761 40.0847 15.4438 39.9964 15.0979C39.9081 14.7519 39.8551 14.4379 39.8331 14.1511C39.811 13.8644 39.8066 13.655 39.8066 13.5275V9.36277H41.74V12.8266C41.74 13.0041 41.7488 13.2362 41.7664 13.5139C41.7841 13.7915 41.8415 14.0737 41.943 14.3514C42.0401 14.6336 42.2034 14.8657 42.4241 15.0569C42.6449 15.2435 42.9583 15.3391 43.3688 15.3391C43.5321 15.3391 43.7087 15.3118 43.8941 15.2572C44.0794 15.2025 44.256 15.1024 44.4193 14.9477C44.5297 14.843 44.6312 14.7064 44.7151 14.5335C44.8872 14.1739 44.9755 13.6914 44.9799 13.0997V9.35821H46.9001V16.964H45.2183V16.9686ZM88 9.36732H85.815V6.75922H83.9081V13.7733C83.9081 14.233 83.9125 14.6472 83.9213 15.0114C83.9302 15.3755 84.0273 15.7351 84.2171 16.0856C84.4245 16.4634 84.7203 16.7319 85.1176 16.9003C85.5104 17.0687 85.9607 17.1598 86.4639 17.1734C86.9671 17.1871 87.4791 17.1416 88 17.0414V15.4301C87.5497 15.4939 87.1436 15.5121 86.7729 15.4848C86.4021 15.462 86.1372 15.3118 85.9651 15.0432C85.8768 14.9021 85.8282 14.711 85.8194 14.4697C85.8106 14.2285 85.8106 13.9599 85.8106 13.6595V10.8876H87.9956V9.37187L88 9.36732ZM57.3218 15.4028C57.9177 15.4028 58.3636 15.1934 58.6549 14.7792C58.9506 14.365 59.0963 13.8279 59.0963 13.1771C59.0963 12.5262 58.9462 11.9618 58.6505 11.5567C58.3547 11.1516 57.9089 10.9513 57.3218 10.9513C56.9157 10.9513 56.5847 11.0469 56.3242 11.2335C56.0638 11.4201 55.874 11.6841 55.746 12.0164C55.6224 12.3487 55.5562 12.7355 55.5562 13.1771C55.5562 13.8553 55.7063 14.3969 56.002 14.7974C56.2977 15.198 56.7392 15.3983 57.3174 15.3983L57.3218 15.4028ZM57.3218 17.1916C56.5802 17.1916 55.9314 17.0187 55.3708 16.6773C54.8102 16.3359 54.3732 15.8625 54.0642 15.2572C53.7508 14.6518 53.5963 13.9599 53.5963 13.1771C53.5963 12.3942 53.7552 11.6841 54.073 11.0833C54.3908 10.4779 54.8323 10.0091 55.3884 9.67228C55.949 9.33546 56.5935 9.16704 57.3218 9.16704C58.0501 9.16704 58.7167 9.34001 59.2773 9.68138C59.8379 10.0228 60.2793 10.4961 60.5927 11.1015C60.9061 11.7023 61.065 12.3987 61.065 13.1816C61.065 13.9645 60.9061 14.6655 60.5927 15.2708C60.2749 15.8716 59.8379 16.345 59.2729 16.6864C58.7123 17.0278 58.0634 17.1962 57.3262 17.1962L57.3218 17.1916ZM35.0128 10.7283C34.7215 10.7283 34.4434 10.7783 34.1697 10.8739C33.9005 10.9695 33.6842 11.1425 33.5253 11.3883C33.3664 11.634 33.2781 11.9618 33.2604 12.3669H36.7299C36.7211 11.9299 36.6372 11.5976 36.4827 11.3564C36.3282 11.1197 36.1252 10.9558 35.8736 10.8648C35.622 10.7738 35.3351 10.7283 35.0172 10.7283H35.0128ZM35.0835 17.2007C34.4081 17.2007 33.7901 17.0596 33.2207 16.7729C32.6557 16.4861 32.201 16.0537 31.8656 15.4665C31.5301 14.8794 31.3624 14.142 31.3624 13.2544V12.9631C31.3624 12.112 31.5213 11.4065 31.8479 10.8421C32.1701 10.2822 32.6027 9.8589 33.1457 9.58125C33.6886 9.29904 34.2889 9.16249 34.9422 9.16249C36.112 9.16249 36.9992 9.48111 37.6083 10.1183C38.2175 10.7556 38.5221 11.6796 38.5221 12.8903V13.7051H33.256C33.2737 14.1602 33.3708 14.5289 33.5429 14.8157C33.7151 15.1024 33.9402 15.3072 34.2095 15.4392C34.4831 15.5712 34.7701 15.635 35.079 15.635C35.5558 15.635 35.9177 15.5621 36.1605 15.4165C36.4033 15.2708 36.5754 15.0569 36.6814 14.7792H38.3808C38.2307 15.5849 37.8599 16.1857 37.2729 16.5954C36.6814 17.0005 35.953 17.2053 35.0835 17.2053V17.2007ZM21.6513 16.9914V6.76377H23.5847L27.9723 13.5822V6.76377H29.9057V16.9868H27.9723L23.5847 10.1684V16.9868H21.6513V16.9914ZM65.329 17.1916C64.7199 17.1916 64.2123 17.1279 63.8017 16.9959C63.3912 16.8639 63.0514 16.6864 62.7865 16.4588C62.5217 16.2312 62.323 15.9718 62.1994 15.685C62.0714 15.3937 61.9964 15.0888 61.9699 14.7701H63.8194C63.868 15.0205 64.0092 15.2253 64.252 15.3846C64.4948 15.5439 64.8567 15.6213 65.329 15.6213C65.872 15.6213 66.2604 15.5439 66.4944 15.3892C66.7283 15.2344 66.8431 15.0387 66.8431 14.7974C66.8431 14.6245 66.7901 14.4788 66.6886 14.3696C66.5871 14.2604 66.4017 14.1602 66.1412 14.0783C65.8808 13.9964 65.51 13.9099 65.0333 13.8234C64.6228 13.7642 64.2476 13.6868 63.9033 13.5867C63.5634 13.4866 63.2588 13.3409 62.9984 13.1589C62.7379 12.9768 62.5349 12.7447 62.3892 12.4716C62.2436 12.1939 62.1729 11.8616 62.1729 11.4656C62.1729 11.0423 62.2965 10.6554 62.5437 10.305C62.7909 9.95903 63.1529 9.68138 63.6208 9.47201C64.0931 9.26263 64.6625 9.16249 65.3246 9.16249C66.0441 9.16249 66.618 9.28084 67.0461 9.50842C67.4743 9.74055 67.7965 10.041 68.0128 10.4051C68.2291 10.7738 68.3571 11.1652 68.4057 11.5794H66.5562C66.4811 11.3018 66.3355 11.0878 66.1236 10.9422C65.9073 10.7965 65.6424 10.7237 65.3246 10.7237C64.8567 10.7237 64.5213 10.7965 64.3226 10.9467C64.1196 11.0969 64.0225 11.2836 64.0225 11.502C64.0225 11.7205 64.1328 11.9117 64.3535 12.0391C64.5742 12.1666 65.0245 12.2758 65.7042 12.3714C66.1236 12.4306 66.5164 12.5125 66.874 12.6263C67.2315 12.7355 67.5493 12.8858 67.8186 13.0678C68.0879 13.2499 68.3042 13.482 68.4542 13.7551C68.6087 14.0282 68.6838 14.365 68.6838 14.761C68.6838 15.2344 68.5602 15.6532 68.313 16.0127C68.0658 16.3769 67.6906 16.6591 67.1918 16.8685C66.693 17.0778 66.0706 17.178 65.3202 17.178L65.329 17.1916ZM73.3318 17.1916C72.5903 17.1916 71.9414 17.0187 71.3808 16.6773C70.8202 16.3359 70.3832 15.8625 70.0742 15.2572C69.7608 14.6518 69.6063 13.9599 69.6063 13.1771C69.6063 12.3942 69.7652 11.6841 70.0831 11.0833C70.4009 10.4779 70.8423 10.0091 71.3985 9.67228C71.9591 9.33546 72.6035 9.16704 73.3318 9.16704C74.0602 9.16704 74.7267 9.34001 75.2873 9.68138C75.8479 10.0228 76.2893 10.4961 76.6027 11.1015C76.9161 11.7023 77.075 12.3987 77.075 13.1816C77.075 13.9645 76.9161 14.6655 76.6027 15.2708C76.2849 15.8716 75.8479 16.345 75.2829 16.6864C74.7223 17.0278 74.069 17.1962 73.3363 17.1962L73.3318 17.1916Z" fill="#1C2C39"/>
                          </g>
                          <defs><clipPath id="clip0_8_2"><rect width="88" height="20" fill="white"/></clipPath></defs>
                        </svg>`
      const logoNSRus = `<svg xmlns="http://www.w3.org/2000/svg" width="97" height="20" viewBox="0 0 97 20" fill="none">
                          <g clip-path="url(#clip0_7_8)">
                            <path d="M8.92754 13.4443C8.01868 13.4443 7.28377 12.6839 7.28377 11.7435C7.28377 10.8031 8.01868 10.0427 8.92754 10.0427C9.8364 10.0427 10.5713 10.8031 10.5713 11.7435C10.5713 12.6839 9.8364 13.4443 8.92754 13.4443ZM17.7639 11.604C17.7639 11.604 17.1681 11.3521 16.9768 11.2441C15.0286 10.1642 13.9415 8.41393 13.9589 6.34419C13.9589 6.2767 13.9589 6.20921 13.9589 6.14172C13.9589 6.07423 13.9632 6.00673 13.9676 5.93924C13.9676 5.87175 13.9763 5.79976 13.9806 5.73227C13.985 5.66478 13.9937 5.59279 13.998 5.52529C14.0067 5.4578 14.0111 5.38581 14.0198 5.31832C14.0285 5.25083 14.0372 5.17884 14.0458 5.11135C14.0545 5.04386 14.0632 4.97186 14.0763 4.90437C14.085 4.83688 14.098 4.76939 14.1111 4.6974C14.1241 4.62991 14.1372 4.56242 14.1502 4.49492C14.1633 4.42743 14.1763 4.35994 14.1894 4.29245C14.2024 4.22496 14.2198 4.15747 14.2328 4.08997C14.2459 4.02248 14.2633 3.95499 14.2763 3.892C14.2937 3.82451 14.3068 3.76152 14.3242 3.69402C14.3416 3.63103 14.3589 3.56354 14.372 3.50055C14.3894 3.43756 14.4068 3.37456 14.4242 3.30707C14.4416 3.24408 14.459 3.18109 14.4764 3.1181C14.4938 3.0551 14.5111 2.99211 14.5285 2.93362C14.5459 2.87513 14.5633 2.81213 14.5807 2.75364C14.5981 2.69515 14.6155 2.63666 14.6373 2.57816C14.6547 2.51967 14.6764 2.46118 14.6938 2.40718C14.7112 2.34869 14.7329 2.2947 14.7503 2.23621C14.7677 2.18221 14.7895 2.12822 14.8069 2.07423C14.8242 2.02023 14.846 1.97074 14.8634 1.91675C14.8808 1.86725 14.8982 1.81326 14.9199 1.76377C14.9373 1.71427 14.9547 1.66478 14.9721 1.61528C14.9895 1.56579 15.0069 1.5208 15.0243 1.4758C15.0417 1.43081 15.0591 1.38581 15.0765 1.34082C15.0939 1.30032 15.1113 1.25533 15.1243 1.21483C15.1417 1.17434 15.1547 1.13384 15.1721 1.09335C15.1852 1.05735 15.2026 1.01686 15.2156 0.980863C15.2287 0.944867 15.2461 0.908872 15.2591 0.872877C15.2722 0.84138 15.2852 0.805385 15.2982 0.773889C15.3113 0.742393 15.3243 0.715396 15.3374 0.6839C15.3504 0.656903 15.3591 0.629907 15.3722 0.60291C15.3809 0.580413 15.3939 0.553416 15.4026 0.530919C15.4113 0.508422 15.42 0.490424 15.4287 0.467927C15.4374 0.449929 15.4461 0.431932 15.4504 0.413934C15.4765 0.355441 15.507 0.292449 15.5244 0.229457V0.215959C15.5418 0.170964 15.5374 0.116971 15.5113 0.076476C15.4896 0.0404805 15.4548 0.00898442 15.4113 -1.44553e-05C15.3678 -0.0135128 15.32 -0.00901333 15.2852 0.0179833C15.2461 0.0404805 15.22 0.0809754 15.1895 0.116971C15.1591 0.157466 15.133 0.197961 15.1069 0.238456C15.0765 0.28795 15.0417 0.332944 15.0112 0.382438C14.9678 0.44543 14.9243 0.512921 14.8764 0.575914C14.8199 0.652404 14.7634 0.733394 14.7068 0.809884C14.6416 0.899873 14.572 0.989862 14.5068 1.07985C14.4285 1.17884 14.3546 1.28233 14.2763 1.38131C14.1894 1.4893 14.1024 1.59729 14.0154 1.70527C13.9197 1.81776 13.8241 1.93474 13.7284 2.04723C13.624 2.16422 13.5197 2.2812 13.4153 2.39819C13.3066 2.51967 13.1935 2.63666 13.0805 2.75364C12.963 2.87513 12.8456 2.99211 12.7239 3.1091C12.6021 3.22608 12.476 3.34307 12.3499 3.45555C12.2238 3.56804 12.089 3.68053 11.9585 3.78851C11.8237 3.8965 11.6889 3.99999 11.5497 4.10347C11.4193 4.19796 11.2845 4.29245 11.1453 4.38244C9.96685 5.14284 9.19715 5.22383 8.90145 5.25533C7.14026 5.4533 5.68783 4.53542 5.3008 4.28345C5.00075 4.08997 4.76592 3.901 4.60937 3.76152C4.50501 3.68053 4.3615 3.77051 4.37455 3.901C4.37455 3.901 4.52675 5.15184 4.54849 5.57029C4.60068 6.47918 4.55719 7.57704 3.9049 8.74689C3.13954 10.1282 1.8741 10.7671 1.561 10.9291C1.13484 11.1451 0.073777 11.6085 0.073777 11.6085C-0.0436353 11.6625 -0.0436353 11.838 0.073777 11.892C0.073777 11.892 1.19572 12.3869 1.561 12.5714C2.44377 13.0124 3.24391 13.5703 3.90055 14.7401C4.67025 16.117 4.55284 17.5118 4.54849 17.9302C4.54849 18.4207 4.37455 19.5995 4.37455 19.5995C4.3615 19.7345 4.50501 19.82 4.60937 19.739C4.60937 19.739 5.58346 18.9831 5.9183 18.7446C6.61843 18.2542 7.58382 17.7413 8.88405 17.7323C10.4322 17.7233 11.6106 18.5377 11.8976 18.7446C12.2325 18.9876 13.2066 19.739 13.2066 19.739C13.3109 19.82 13.4544 19.73 13.4414 19.5995C13.4414 19.5995 13.2848 18.3487 13.2674 17.9302C13.224 16.9404 13.2587 15.919 13.9197 14.7401C14.6894 13.3678 15.9462 12.7289 16.2593 12.5714C16.6811 12.3554 17.7465 11.892 17.7465 11.892C17.8639 11.838 17.8639 11.6625 17.7465 11.6085L17.7639 11.604Z" fill="#0AADF9"/>
                            <path d="M43.4905 6.67715C43.4818 7.0596 43.1818 7.37006 42.8078 7.37006C42.4338 7.37006 42.1338 7.0596 42.1294 6.67715H40.9379C40.9466 7.74352 41.7815 8.60291 42.8122 8.60291C43.8428 8.60291 44.6777 7.74352 44.6864 6.67715H43.4949H43.4905ZM75.5528 15.2216C76.1399 15.2216 76.5791 15.0146 76.8661 14.6052C77.1574 14.1957 77.3009 13.6693 77.3009 13.0214C77.3009 12.3734 77.1531 11.82 76.8617 11.4196C76.566 11.0191 76.1312 10.8211 75.5528 10.8211C75.1527 10.8211 74.8266 10.9156 74.57 11.1001C74.3134 11.2846 74.1265 11.5455 74.0003 11.874C73.8786 12.2025 73.8134 12.5849 73.8134 13.0214C73.8134 13.6918 73.9612 14.2272 74.2526 14.6232C74.5439 15.0191 74.9788 15.2171 75.5485 15.2171L75.5528 15.2216ZM75.5528 16.9899C74.8222 16.9899 74.183 16.8189 73.6307 16.4814C73.0784 16.144 72.6523 15.676 72.3435 15.0821C72.0348 14.4882 71.8826 13.7998 71.8826 13.0259C71.8826 12.252 72.0391 11.5545 72.3522 10.9561C72.6653 10.3622 73.1002 9.89425 73.6481 9.56129C74.2004 9.22833 74.8353 9.06185 75.5528 9.06185C76.2703 9.06185 76.927 9.23283 77.4792 9.57029C78.0315 9.90775 78.462 10.3757 78.7751 10.9741C79.0839 11.568 79.2404 12.2565 79.2404 13.0304C79.2404 13.8043 79.0839 14.4972 78.7751 15.0911C78.462 15.685 78.0315 16.153 77.4749 16.4904C76.9226 16.8279 76.2834 16.9944 75.5572 16.9944L75.5528 16.9899ZM59.4891 15.2216C60.0761 15.2216 60.5153 15.0146 60.8023 14.6052C61.0937 14.1957 61.2372 13.6693 61.2372 13.0214C61.2372 12.3734 61.0893 11.82 60.798 11.4196C60.5066 11.0191 60.0674 10.8211 59.4891 10.8211C59.089 10.8211 58.7628 10.9156 58.5063 11.1001C58.2497 11.2846 58.0627 11.5455 57.9366 11.874C57.8148 12.2025 57.754 12.5849 57.754 13.0214C57.754 13.6918 57.9018 14.2272 58.1932 14.6232C58.4889 15.0191 58.9194 15.2171 59.4891 15.2171V15.2216ZM59.4891 16.9899C58.7585 16.9899 58.1192 16.8189 57.567 16.4814C57.0147 16.144 56.5885 15.676 56.2798 15.0821C55.971 14.4882 55.8188 13.7998 55.8188 13.0259C55.8188 12.252 55.9754 11.5545 56.2885 10.9561C56.6016 10.3622 57.0364 9.89425 57.5844 9.56129C58.1366 9.22833 58.7715 9.06185 59.4891 9.06185C60.2066 9.06185 60.8632 9.23283 61.4155 9.57029C61.9678 9.90775 62.4026 10.3757 62.7114 10.9741C63.0201 11.568 63.1767 12.2565 63.1767 13.0304C63.1767 13.8043 63.0201 14.4972 62.7114 15.0911C62.3983 15.685 61.9678 16.153 61.4155 16.4904C60.8632 16.8279 60.224 16.9944 59.4978 16.9944L59.4891 16.9899ZM67.6688 16.9899C66.9947 16.9899 66.3859 16.8459 65.8424 16.5534C65.2988 16.261 64.8683 15.829 64.5508 15.2486C64.2334 14.6727 64.0725 13.9527 64.0725 13.0888V12.8009C64.0725 11.9775 64.2377 11.2891 64.5682 10.7356C64.8987 10.1822 65.3379 9.75927 65.8815 9.4758C66.4251 9.18784 67.0208 9.04386 67.6644 9.04386C68.3559 9.04386 68.9386 9.16534 69.4213 9.40381C69.8996 9.64228 70.2736 9.97074 70.5345 10.3937C70.7954 10.8166 70.9476 11.2801 70.9824 11.784H69.1299C69.056 11.4421 68.9081 11.1631 68.682 10.9606C68.4559 10.7536 68.1167 10.6547 67.6644 10.6547C67.3513 10.6547 67.0643 10.7311 66.7991 10.8841C66.5381 11.0371 66.3251 11.2711 66.1642 11.586C66.0033 11.901 65.9206 12.3059 65.9206 12.8054V13.0933C65.9206 13.6288 65.9946 14.0652 66.1468 14.4072C66.299 14.7446 66.5077 14.9966 66.7686 15.1541C67.0295 15.3116 67.3296 15.3926 67.6601 15.3926C68.021 15.3926 68.3298 15.2936 68.595 15.1001C68.856 14.9021 69.0342 14.6232 69.1256 14.2632H70.9781C70.9215 14.8076 70.7476 15.2891 70.4519 15.712C70.1562 16.135 69.7692 16.4544 69.2821 16.6749C68.7994 16.8954 68.2558 17.0034 67.6557 17.0034L67.6688 16.9899ZM34.5802 10.5917C34.2932 10.5917 34.0193 10.6412 33.7497 10.7356C33.4801 10.8301 33.2713 11.0011 33.1148 11.2441C32.9582 11.487 32.8712 11.811 32.8539 12.2115H36.2719C36.2632 11.7795 36.1805 11.4466 36.0283 11.2126C35.8761 10.9786 35.6761 10.8166 35.4282 10.7266C35.1804 10.6367 34.8977 10.5917 34.5846 10.5917H34.5802ZM34.6498 16.9899C33.9845 16.9899 33.3757 16.8504 32.8191 16.5669C32.2624 16.2835 31.8145 15.856 31.484 15.2756C31.1535 14.6996 30.9883 13.9662 30.9883 13.0888V12.8009C30.9883 11.9595 31.1448 11.2621 31.4666 10.7086C31.7841 10.1552 32.2103 9.73677 32.7451 9.4623C33.28 9.18334 33.8714 9.04386 34.515 9.04386C35.6674 9.04386 36.5415 9.35882 37.1416 9.99324C37.7417 10.6232 38.0417 11.5365 38.0417 12.7334V13.5388H32.8539C32.8712 13.9887 32.9669 14.3532 33.1365 14.6367C33.3061 14.9201 33.5279 15.1226 33.7931 15.2531C34.0628 15.3836 34.3454 15.4466 34.6498 15.4466C35.1195 15.4466 35.4761 15.3746 35.7152 15.2306C35.9544 15.0866 36.124 14.8751 36.2284 14.6007H37.9026C37.7547 15.3971 37.3894 15.991 36.8111 16.3959C36.2284 16.7964 35.5108 16.9989 34.6542 16.9989L34.6498 16.9899ZM92.6733 16.7919V10.9741H90.225V9.25533H97.0088V10.9741H94.5606V16.7919H92.6776H92.6733ZM85.7024 15.2396C86.059 15.3026 86.3634 15.2891 86.6156 15.2036C86.8679 15.1181 87.0766 14.9741 87.2332 14.7716C87.3941 14.5692 87.5071 14.3172 87.581 14.0202C87.655 13.7188 87.6898 13.3858 87.6898 13.0124C87.6898 12.6389 87.655 12.3014 87.5854 12C87.5158 11.703 87.4071 11.4511 87.2549 11.2486C87.1027 11.0461 86.8983 10.9066 86.6417 10.8211C86.3852 10.7401 86.0721 10.7311 85.6981 10.7986V15.2351L85.7024 15.2396ZM84.0413 15.2396V10.8031C83.6673 10.7356 83.3542 10.7491 83.0976 10.8346C82.8411 10.9201 82.6367 11.0686 82.4845 11.2711C82.3323 11.4736 82.2236 11.7255 82.154 12.0225C82.0844 12.3194 82.0496 12.6479 82.0496 13.0124C82.0496 13.3768 82.0844 13.7053 82.1583 14.0022C82.2323 14.2992 82.3497 14.5512 82.5062 14.7581C82.6671 14.9651 82.8715 15.1091 83.1237 15.1991C83.3759 15.2846 83.6803 15.2981 84.0369 15.2396H84.0413ZM84.0413 19.9865V16.9449C83.3107 17.0619 82.6541 16.9854 82.0627 16.7109C81.4713 16.4364 81.006 15.9865 80.6581 15.3611C80.3102 14.7356 80.1406 13.9572 80.1406 13.0169C80.1406 12.0765 80.3145 11.2936 80.6581 10.6682C81.006 10.0427 81.4713 9.59279 82.0627 9.31832C82.6541 9.04835 83.3107 8.96736 84.0413 9.08435V6.67715L85.7024 6.66815V9.08435C86.433 8.96736 87.0897 9.04385 87.6811 9.31832C88.2725 9.59279 88.7378 10.0427 89.0857 10.6682C89.4335 11.2936 89.6031 12.0765 89.6031 13.0169C89.6031 13.9572 89.4292 14.7356 89.0857 15.3611C88.7378 15.9865 88.2725 16.4364 87.6811 16.7109C87.0897 16.9854 86.4287 17.0619 85.7024 16.9449V19.9865H84.0413ZM52.74 14.3082C52.6139 14.6637 52.4226 14.9471 52.166 15.1586C51.9094 15.3701 51.5746 15.4736 51.1702 15.4736C50.7658 15.4736 50.4135 15.3746 50.1613 15.1811C49.9091 14.9876 49.7308 14.7131 49.6177 14.3577C49.509 14.0022 49.4525 13.5928 49.4525 13.1248C49.4525 12.6569 49.509 12.2475 49.6177 11.892C49.7308 11.5365 49.9047 11.2621 50.1483 11.0686C50.3918 10.8706 50.7092 10.7761 51.1006 10.7761C51.5181 10.7761 51.866 10.8841 52.1356 11.0956C52.4052 11.3071 52.6052 11.5905 52.7357 11.946C52.8661 12.3014 52.9314 12.6929 52.9314 13.1248C52.9314 13.5568 52.8705 13.9527 52.7444 14.3082H52.74ZM54.4795 10.9696C54.1881 10.3712 53.775 9.90325 53.2445 9.55679C52.7139 9.21483 52.0921 9.03936 51.3746 9.03936C50.657 9.03936 50.0656 9.21483 49.5829 9.56129C49.5351 9.59729 49.496 9.63328 49.4525 9.66928V9.25083H47.7913V19.9955H49.6873V16.5084C50.1743 16.8234 50.7658 16.9854 51.4746 16.9854C52.1834 16.9854 52.7661 16.8099 53.2792 16.4634C53.7967 16.117 54.1968 15.64 54.4882 15.0416C54.7752 14.4432 54.9187 13.7638 54.9187 13.0124C54.9187 12.261 54.7708 11.5635 54.4795 10.9651V10.9696ZM46.1388 9.25533V16.7919H44.3385V12.189L40.9597 16.7919H39.4637V9.25533H41.2641V13.7053L44.656 9.25533H46.1388ZM21.33 16.7919V6.67715H23.213V10.8346H27.6703V6.67715H29.5533V16.7919H27.6703V12.6209H23.213V16.7919H21.33Z" fill="#1C2C39"/>
                          </g>
                          <defs><clipPath id="clip0_7_8"><rect width="97" height="20" fill="white"/></clipPath></defs>
                        </svg>`
      const lineColor = '#5F5F61'
      const docDefinition = {
        pageMargins: [40, 100, 40, 60],
        header: {
          margin: [40, 40, 40, 40],
          stack: [
            {
              alignment: 'justify',
              columns: [
                {
                  width: 250,
                  style: 'header',
                  text: [
                    this.localeRU
                      ? `ОТЧЕТ О ПРОХОЖДЕНИИ\n КУРСА №${this.course.id}`
                      : `COURSE №${this.course.id} REPORT`
                  ]
                },
                {
                  width: 130,
                  text: [
                    {
                      style: 'bold',
                      text: `${this.$t('entity.patient')}: \n`
                    },
                    {
                      text: `${this.fullNamePatient}`
                    }
                  ]
                },
                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `${this.$t('personInfo.6786')}: `
                        },
                        {
                          text: `${this.gender}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `${this.$t('personInfo.age')}: `
                        },
                        {
                          text: `${this.age}\n`
                        }
                      ]
                    }
                  ]
                },

                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `${this.$t('personInfo.height')}: `
                        },
                        {
                          text: `${this.height}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `${this.$t('personInfo.weight')}: `
                        },
                        {
                          text: `${this.weight}\n`
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            `\n`,
            {
              canvas: [
                {
                  lineColor: lineColor,
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 515,
                  y2: 0,
                  lineWidth: 2
                }
              ]
            },
            `\n`
          ]
        },
        footer: {
          columns: [
            {
              svg: this.localeRU ? logoNSRus : logoNSEng,
              margin: [40, 15, 0, 0]
            },
            {
              text: this.localeRU ? 'Аккордикс' : 'Accordix',
              alignment: 'right',
              style: 'titleIcon',
              margin: [0, 20, 40, 0]
            }
          ]
        },
        info: {
          title: this.localeRU
            ? `Отчет о прохождении курса ${this.course.id}`
            : `Course ${this.course.id} report`
        },
        content: [
          {
            alignment: 'justify',
            columns: [
              {
                text: [
                  `${this.$t('src/components/courseinfoblock.9038')}: ${this.getSurnameWithInitials(
                    this.course.doctor
                  )}\n\n`,
                  `${this.$t('general.scenario')}: ${this.courseType} \n`
                ]
              },
              {
                text: [
                  `${this.$t('src/components/protocol.startCourse')}: ${this.courseDate}\n\n`,
                  `${this.$t('src/components/protocol.finalCourse')}: ${this.courseCloseDate}\n`
                ]
              },
              {
                text: [
                  `${this.$t('src/components/protocol.sessionsCompleted')}: ${
                    this.trainingsInfo.done
                  } ${this.$t('general.outOf')} ${this.trainingsInfo.all}\n\n`,
                  `${this.$t('src/components/protocol.sessionsMissed')}: ${this.trainingsInfo.missed}\n`
                ]
              }
            ]
          },
          '\n\n',
          {
            text: `${this.$t('src/components/coursesettingsblock.9828')}`,
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: lineColor,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                width: 100,
                text: [
                  `${this.$t('src/components/coursesettingsblock.9095')} ${this.hrValue}\n\n`,
                  `${this.$t('src/components/coursesettingsblock.sT')} ${this.optCalcST}\n`
                ]
              },
              {
                width: 140,
                text: [
                  `${this.$t('src/components/coursesettingsblock.6547')} ${this.optArrhythmia}\n\n`,
                  `${this.$t('sessionsInfo.borgScale')}: ${this.optBorg} \n`
                ]
              },
              {
                width: 165,
                text: [
                  `${this.$t('general.duration')}: ${this.durationSettingString}\n\n`,
                  `${this.$t('src/components/coursesettingsblock.sessionCompletion')} ${
                    this.optAutoSessionStop
                  } \n`
                ]
              },
              {
                width: 125,
                text: [
                  `${this.$t('src/components/coursesettingsblock.cardioEvents')}: ${this.optShowEvents}\n`
                ]
              }
            ]
          },
          '\n\n',
          {
            text: `${this.$t('src/components/training/trainingmanage.4393')}`,
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: lineColor,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            style: 'table',
            table: {
              heights: 13,
              body: this.body
            },
            layout: {
              defaultBorder: false,
              fillColor: function(rowIndex) {
                return rowIndex % 2 === 0 ? '#D7F1E7' : null
              }
            }
          }
        ],
        defaultStyle: {
          fontSize: 9
        },
        styles: {
          header: {
            fontSize: 15
          },
          title: {
            fontSize: 10,
            bold: true,
            margin: [0, 0, 0, 3]
          },
          titleIcon: {
            fontSize: 14,
            bold: true
          },
          table: {
            margin: [0, 7, 5, 15],
            alignment: 'center'
          },
          tableHeader: {
            bold: true,
            margin: [5, 5, 5, 5]
          },
          boldMargin: {
            bold: true,
            margin: [0, 0, 0, 2]
          },
          mwt: {
            margin: 6,
            background: '#eeeeee'
          }
        }
      }
      const graphComponent = this.$parent.$parent.$refs.manage.$refs.dinamic.$refs.graph
      const chartStandard = graphComponent.chart
      const legendName = graphComponent.listOfLegend[graphComponent.selectedLegend]
      if (chartStandard.data.length) {
        let imgStandard = await chartStandard.exporting.getImage('jpg')

        docDefinition.content.push({
          stack: [
            {
              text: `${this.$t('src/components/protocol.standardTrain')}`,
              style: 'boldMargin'
            },
            {
              canvas: [
                {
                  lineColor: lineColor,
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 515,
                  y2: 0,
                  lineWidth: 1.5
                }
              ]
            },
            `\n\n`,
            {
              text: legendName,
              style: 'boldMargin'
            },
            `\n\n`,
            {
              image: imgStandard,
              fit: [500, 300]
            },
            `\n\n`
          ],
          unbreakable: true
        })
      }
      docDefinition.content.push({
        stack: [
          {
            text: `${this.$t('src/pages/coursesedit.doctorsReport')}`,
            style: 'boldMargin'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: lineColor,
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            text: this.conclusion
          }
        ]
      })
      if (window.navigator.pdfViewerEnabled) {
        pdfMake.createPdf(docDefinition).open()
      } else {
        pdfMake.createPdf(docDefinition).download()
      }
    },
    saturationStr(train) {
      if (train.events) {
        let parsedEvent = JSON.parse(train.events)
        let saturation = parsedEvent.filter(item => item.episodeType === 'eventSpO2')
        if (saturation.length) {
          let sum = 0
          saturation.forEach(item => (sum += +item.value))
          return `${sum / saturation.length}%`
        } else return '–'
      } else {
        return '–'
      }
    }
  }
}
</script>
